import React, { useCallback, useEffect, useRef, memo, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.scss';
import { files } from '../../api/files';
import { useStorageUrl } from '../../hooks/useStorageUrl';
import { useLoader } from '../../contexts/LoaderContext';
import { useAlert } from '../../contexts/AlertContext';

interface RichTextEditorProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  touched?: boolean;
  placeholder?: string;
}

// TODO: Move this utility function to a shared utils folder if it's needed elsewhere
const decodeHtmlEntities = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return html;
  }

  const textArea = document.createElement('textarea');
  textArea.innerHTML = html;
  return textArea.value;
};

export const RichTextEditor: React.FC<RichTextEditorProps> = memo(
  ({ id, name, label, value, onChange, onBlur, error, touched, placeholder }) => {
    const quillRef = useRef<ReactQuill>(null);
    const [isStorageReady, setIsStorageReady] = useState(false);
    const decodedValue = decodeHtmlEntities(value);

    const { storageUrl, isLoading: isStorageUrlLoading } = useStorageUrl();
    const { showLoader, hideLoader } = useLoader();
    const { showAlert } = useAlert();

    const handlePaste = useCallback((e: ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData?.getData('text/plain') || '';
      const editor = quillRef.current?.getEditor();
      if (editor && text) {
        const range = editor.getSelection(true);
        if (range) {
          editor.insertText(range.index, text, 'user');
          editor.setSelection(range.index + text.length, 0, 'user');
        }
      }
    }, []);

    // Check when storage is ready
    useEffect(() => {
      if (!isStorageUrlLoading && storageUrl) {
        setIsStorageReady(true);
      } else {
        setIsStorageReady(false);
      }
    }, [storageUrl, isStorageUrlLoading]);

    useEffect(() => {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const editorElement = editor.root;
        editorElement.addEventListener('paste', handlePaste);
        return () => {
          editorElement.removeEventListener('paste', handlePaste);
        };
      }
    }, [handlePaste]);

    const imageHandler = useCallback(async () => {
      if (!isStorageReady) {
        // Try to get storage URL again if not ready
        if (storageUrl) {
          setIsStorageReady(true);
        } else {
          showAlert('File storage is not ready yet. Please try again later.', 'warning');
          console.warn('Image upload cancelled: storage URL not ready.');
          return;
        }
      }

      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();

      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) {
          return;
        }

        const editor = quillRef.current?.getEditor();
        if (!editor) {
          console.error('Quill editor instance not found.');
          showAlert('An editor error occurred.', 'error');
          return;
        }

        showLoader();
        try {
          const response = await files.uploadImage(file);

          const range = editor.getSelection(true);
          const imageUrl = `${storageUrl}/${response.fileKey}`;

          if (range) {
            editor.insertEmbed(range.index, 'image', imageUrl);
            editor.setSelection(range.index + 1, 0, 'user');
          } else {
            const length = editor.getLength();
            editor.insertEmbed(length - 1, 'image', imageUrl);
            editor.setSelection(length, 0, 'user');
          }
        } catch (err) {
          console.error('Failed to upload image:', err);
          showAlert('Failed to upload image. Please try again.', 'error');
        } finally {
          hideLoader();
        }
      };
    }, [storageUrl, isStorageReady, showLoader, hideLoader, showAlert]);

    const modules = useRef({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }).current;

    const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'image'];

    if (isStorageUrlLoading) {
      return (
        <div className="form-field">
          <label htmlFor={id}>{label}</label>
          <div className="rich-text-editor-wrapper loading">
            <div className="editor-loading-placeholder">Loading editor...</div>
          </div>
        </div>
      );
    }

    return (
      <div className={`form-field ${touched && error ? 'error' : ''}`}>
        <label htmlFor={id}>{label}</label>
        <div className="rich-text-editor-wrapper">
          <ReactQuill
            ref={quillRef}
            id={id}
            theme="snow"
            value={decodedValue}
            onChange={onChange}
            onBlur={onBlur}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
          />
        </div>
        {touched && error && <div className="error-message">{error}</div>}
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
